import { constants, termsOfAgreementParser } from '@oresundsbron/validators';
import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';
import { useValidateCampaign } from '../../../hooks/useValidateCampaign';
import { getDefaultCurrency, toCurrency } from '../../../lib/currency';
import { validate } from '../../../lib/zod';
import { useContractsStore } from '../../../stores/contracts';
import { ContractType } from '@oresundsbron/api/src/backend/Contract';
import {
  TermsOfAgreement as Business,
  Data,
} from '../../../components/Forms/TermsOfAgreement/Business';
import { useSubscribe } from '@oresundsbron/use-form';
import { Data as PaymentContract } from '../../../components/Forms/PaymentContract';
import { pipe } from 'fp-ts/lib/function';
import {
  getContractTranslationKey,
  TranslateableContracts,
} from '../../../lib/contract-translations';
import { getOrElse, map, sequenceArray } from 'fp-ts/lib/Option';
import { toArray } from 'fp-ts/lib/ReadonlyArray';
import { findFirst, map as mapA } from 'fp-ts/lib/Array';
import { Currency } from '@oresundsbron/type-safeguards';
import { useRouter } from 'next/router';
import { TermsOfAgreementText } from '../../../components/TermsOfAgreementText';
import { SignUpFormPart } from '../utils';

export const TermsOfAgreement: FC<
  SignUpFormPart & {
    contract: ContractType;
  }
> = ({ namespace, titleTranslationKey, contract, ns }) => {
  const { locale } = useRouter();
  const { t } = useTranslation(ns, {
    nsMode: 'fallback',
  });

  const { contracts, main } = useContractsStore();

  const [currency] = useSubscribe<Data>('termsOfAgreement', 'currency');

  const displayCurrency = useMemo(
    () => (currency as Currency) || getDefaultCurrency(locale || ''),
    [currency, locale]
  );

  const validateCampaignCode = useValidateCampaign({
    contract,
    t,
  });

  const providers = useMemo(
    () => [
      {
        label: t('oresundpay'),
        value: 'oresundpay',
      },
      {
        label: 'Brobizz',
        value: 'brobizz',
      },
      {
        label: 'DKV',
        value: 'dkv',
      },
      {
        label: 'Flytpass',
        value: 'flytpass',
      },
      {
        label: 'AS24/Total',
        value: 'as24total',
      },
      {
        label: 'Axxes',
        value: 'axxes',
      },
      {
        label: 'Skyttelpass',
        value: 'skyttelpass',
      },
      {
        label: 'Toll4Europe',
        value: 'toll4europe',
      },
      {
        label: 'Tollticket',
        value: 'tollticket',
      },
      {
        label: 'Eurowag',
        value: 'eurowag',
      },
      {
        label: 'Partners Brobizz',
        value: 'partners_brobizz',
      },
      {
        label: 'AutoSync AS',
        value: 'fremtind',
      },
    ],
    [t]
  );

  const [provider, oresundsPay] = useSubscribe<PaymentContract>(
    'paymentAgreement',
    'paymentProvider',
    'oresundsPay'
  );
  const oresundPay: boolean = oresundsPay === 'true' ? true : false;

  const paymentProvider = providers.find((p) => p.value === provider)?.label;

  const c = useMemo(
    () =>
      pipe(
        contracts,
        mapA((c) =>
          pipe(
            c.value as TranslateableContracts,
            getContractTranslationKey,
            map((k) => ({
              value: t('price.yearly', {
                price: pipe(
                  c.prices || [],
                  findFirst((x) => x.currency === displayCurrency),
                  map((p) => toCurrency(p.price || 0, locale, displayCurrency)),
                  getOrElse(() => '')
                ),
              }),
              label: k,
              discount: false,
            }))
          )
        ),
        sequenceArray,
        map(toArray),
        getOrElse(() => [] as { label: string; value: string }[])
      ),
    [contracts, t, displayCurrency, locale]
  );

  return (
    <Business
      namespace={namespace}
      title={t(titleTranslationKey)}
      defaultValues={{ currency: getDefaultCurrency(locale || '') }}
      contracts={c}
      total={t('price.yearly', {
        price: pipe(
          main.prices || [],
          findFirst((x) => x.currency === displayCurrency),
          map((p) => toCurrency(p.price || 0, locale, displayCurrency)),
          getOrElse(() => '')
        ),
      })}
      labels={{
        loading: t('action.loading'),
        campaignCode: t('action.campaignCode'),
        agreement: t('form.labels.termsOfAgreement'),
        currency: t('form.labels.currency'),
        total: t('signup.total'),
        yourOrder: t('form.sections.termsOfAgreement.subTitle'),
        contract: t('form.labels.contract'),
      }}
      placeholders={{
        currency: t('form.placeholders.currency'),
      }}
      currencyOptions={[
        { label: 'SEK', value: 'SEK' },
        { label: 'DKK', value: 'DKK' },
      ]}
      helpText={
        <TermsOfAgreementText
          type={constants.OresundBusiness}
          isOresundPay={oresundPay}
        />
      }
      paymentProviderText={t('form.sections.termsOfAgreement.paymentProvider', {
        paymentProvider,
      })}
      validateCampaignCode={validateCampaignCode}
      onValidate={validate(termsOfAgreementParser.spa)}
    />
  );
};
